import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';

import { VisitFilterCountField } from './VisitFilterCountField';
import { VisitFilterOperatorField } from './VisitFilterOperatorField';
import { VisitFilterTimeframeField } from './VisitFilterTimeframeField';
import {
  VisitCountFilterValue,
  VisitCountTimeframeDays,
  VisitFilterDirection,
} from './_types';
import { VisitFilterSelectOption } from './constants';
import { useClientVisitFilterLabels } from './useClientVisitFilterLabels';
import { useSyncedVisitFilterDraft } from './useSyncedVisitFilterDraft';
import {
  getVisitCountFilterValidationErrorKey,
  isVisitCountFilterComplete,
} from './visitFilterValidation';

interface VisitCountFilterFieldsProps {
  value: VisitCountFilterValue | null | undefined;
  onChange: (value: VisitCountFilterValue | null) => void;
  timeframeOptions: VisitFilterSelectOption<number>[];
  direction?: VisitFilterDirection;
  operatorLabel?: string;
  countLabel?: string;
}

const isVisitCountDraftEmpty = (draft: VisitCountFilterValue | null) =>
  !draft?.operator && draft?.value == null && !draft?.timeframeDays;

export const VisitCountFilterFields = ({
  value,
  onChange,
  timeframeOptions,
  direction = 'past',
  operatorLabel,
  countLabel,
}: VisitCountFilterFieldsProps) => {
  const { getValidationError } = useClientVisitFilterLabels();
  const allowedTimeframeDays = timeframeOptions.map((option) => option.value);

  const { draft, patchDraft } = useSyncedVisitFilterDraft<VisitCountFilterValue>({
    value,
    onChange,
    isEmpty: isVisitCountDraftEmpty,
    isComplete: (next) => isVisitCountFilterComplete(next, allowedTimeframeDays),
  });

  const validationErrorKey = getVisitCountFilterValidationErrorKey(
    draft,
    allowedTimeframeDays
  );
  const validationError = validationErrorKey
    ? getValidationError(validationErrorKey)
    : null;
  const showErrors = Boolean(validationError);
  const isComplete = isVisitCountFilterComplete(draft, allowedTimeframeDays);

  const mergeDraft = (
    current: VisitCountFilterValue | null,
    patch: Partial<VisitCountFilterValue>
  ): VisitCountFilterValue => ({
    ...(current ?? {}),
    ...patch,
    direction,
  });

  return (
    <Stack spacing={2}>
      <VisitFilterOperatorField
        value={draft?.operator}
        label={operatorLabel}
        onChange={(operator) => patchDraft({ operator }, mergeDraft)}
        error={showErrors && !draft?.operator}
      />
      <VisitFilterCountField
        label={countLabel}
        value={draft?.value}
        onChange={(count) => patchDraft({ value: count }, mergeDraft)}
        error={showErrors && draft?.value == null}
      />
      <VisitFilterTimeframeField
        value={draft?.timeframeDays}
        options={timeframeOptions}
        onChange={(timeframeDays) =>
          patchDraft(
            { timeframeDays: timeframeDays as VisitCountTimeframeDays },
            mergeDraft
          )
        }
        error={showErrors && !draft?.timeframeDays}
      />
      {validationError && !isComplete && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {validationError}
        </Typography>
      )}
    </Stack>
  );
};
